import { Container, Graphics } from 'pixi.js';
import type { Island } from './world.js';
import { WORLD_W, WORLD_H } from './world.js';
import type { RemotePlayer } from './net.js';

const MAP_SIZE = 160; // pixels
const PADDING  = 14;  // from screen edge
const BG_ALPHA = 0.60;
const SCALE    = MAP_SIZE / Math.max(WORLD_W, WORLD_H); // world units → map pixels

export class Minimap {
  container: Container;
  private gfx: Graphics;
  // Pre-scaled island polygons (relative to map origin 0,0)
  private islandPolys: { x: number; y: number }[][];
  private builtIslandPolys: { x: number; y: number }[][] = [];

  constructor(islands: Island[]) {
    this.container = new Container();
    this.gfx = new Graphics();
    this.container.addChild(this.gfx);

    this.islandPolys = islands.map(isl =>
      isl.points.map(p => ({ x: p.x * SCALE, y: p.y * SCALE })),
    );
  }

  /** Replace the drawn set of player-built islands (small, changes rarely). */
  setBuiltIslands(islands: Island[]): void {
    this.builtIslandPolys = islands.map(isl =>
      isl.points.map(p => ({ x: p.x * SCALE, y: p.y * SCALE })),
    );
  }

  update(
    screenW: number,
    screenH: number,
    localPos: { x: number; y: number },
    localColor: number,
    remotePlayers: Map<string, RemotePlayer>,
  ): void {
    const g = this.gfx;
    g.clear();

    // Position map in bottom-right corner
    const ox = screenW - MAP_SIZE - PADDING;
    const oy = screenH - MAP_SIZE - PADDING;

    // Background
    g.rect(ox, oy, MAP_SIZE, MAP_SIZE);
    g.fill({ color: 0x0a3a50, alpha: BG_ALPHA });
    g.rect(ox, oy, MAP_SIZE, MAP_SIZE);
    g.stroke({ color: 0xffffff, alpha: 0.25, width: 1 });

    // Islands — draw actual polygon shapes
    for (const poly of this.islandPolys) {
      if (poly.length < 2) continue;
      g.moveTo(ox + poly[0].x, oy + poly[0].y);
      for (let i = 1; i < poly.length; i++) {
        g.lineTo(ox + poly[i].x, oy + poly[i].y);
      }
      g.closePath();
      g.fill({ color: 0x4a9a5a, alpha: 0.90 });
      g.stroke({ color: 0x2a5a35, alpha: 0.6, width: 0.5 });
    }

    // Player-built islands — same shape, distinct color
    for (const poly of this.builtIslandPolys) {
      if (poly.length < 2) continue;
      g.moveTo(ox + poly[0].x, oy + poly[0].y);
      for (let i = 1; i < poly.length; i++) {
        g.lineTo(ox + poly[i].x, oy + poly[i].y);
      }
      g.closePath();
      g.fill({ color: 0x40c080, alpha: 0.90 });
      g.stroke({ color: 0x2a5a35, alpha: 0.6, width: 0.5 });
    }

    // Remote players — small dot with boat color
    for (const p of remotePlayers.values()) {
      const px = ox + (p.ashore ? p.personX : p.x) * SCALE;
      const py = oy + (p.ashore ? p.personY : p.y) * SCALE;
      g.circle(px, py, 2);
      g.fill({ color: p.color, alpha: 1 });
      g.circle(px, py, 2);
      g.stroke({ color: 0x000000, alpha: 0.5, width: 0.8 });
    }

    // Local player — slightly larger dot
    const lx = ox + localPos.x * SCALE;
    const ly = oy + localPos.y * SCALE;
    g.circle(lx, ly, 2.5);
    g.fill({ color: localColor, alpha: 1 });
    g.circle(lx, ly, 2.5);
    g.stroke({ color: 0x000000, alpha: 0.6, width: 1 });
  }
}
